import { statusColors } from '@/const';

interface MachineEntity {
  [key: string]: string;
}

interface StatusData {
  value: number;
  name: string;
  itemStyle: {
    color: string;
  };
}

export const countMachineStatusData = (entities: Array<MachineEntity>): [StatusData[], Record<string, Array<[string, string][]>>] => {
  const status: Record<string, Array<[string, string][]>> = {
    critical: [],
    alarm: [],
    monitor: [],
    healthy: [],
    na: [],
  };
  const data: StatusData[] = [];

  for (const item of entities) {
    const machineStatus = Object.values(item)[0];
    status[machineStatus].push(Object.entries(item));
  }

  for (const key in status) {
    const length = status[key].length;
    data.push({
      value: length,
      name: key,
      itemStyle: {
        color: statusColors[key],
      },
    });
  }

  return [data, status];
};

export enum BI {
  Cost = 'cost',
  Hours = 'hours',
  Time = 'time',
  DownTime = 'downtime',
}

export const BIDefaults = {
  DEFAULT_PERIOD: '1 year',
  DEFAULT_DATA_TYPE: 'Time',
};

interface ChartDataItem {
  monthName: string;
  current?: number;
  accumulated?: number;
}

interface ChartOptions {
  dataType: string;
  currency: string;
  current: ChartDataItem[];
  accumulated: ChartDataItem[];
  gridHeight: number;
}

export const getOption = ({ dataType, currency, current, accumulated, gridHeight }: ChartOptions) => {
  const unit = dataType === 'cost' ? currency : '';

  const formatNumber = (value: number) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: Array<{ name: string; seriesName: string; value: number }>) => {
        const month = params[0].name;
        const currentParam = params.find(param => param.seriesName === 'Current');
        const accumulatedParam = params.find(param => param.seriesName === 'Accumulated');
        let tooltipContent = `<b>${month}</b><br/>`;
        if (currentParam) {
          const currentValue =
            dataType === 'cost'
              ? `${unit}${formatNumber(currentParam.value)}`
              : `${currentParam.value} ${currentParam.value > 1 ? 'hours' : 'hour'}`;
          tooltipContent += `${currentParam.seriesName}: ${currentValue}<br/>`;
        }
        if (accumulatedParam) {
          const accumulatedValue =
            dataType === 'cost'
              ? `${unit}${formatNumber(accumulatedParam.value)}`
              : `${accumulatedParam.value} ${accumulatedParam.value > 1 ? 'hours' : 'hour'}`;
          tooltipContent += `${accumulatedParam.seriesName}: ${accumulatedValue}`;
        }
        return tooltipContent;
      },
    },
    xAxis: {
      type: 'category',
      data: current?.map(item => item.monthName),
      axisLabel: {
        formatter: (value: string) => value,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => {
          if (dataType === 'cost') {
            const formattedValue = value >= 1000 ? `${Math.floor(value / 1000)}k` : value;
            return `${unit}${formattedValue}`;
          } else {
            const hoursText = value > 1 ? 'hours' : 'hour';
            return `${value} ${hoursText}`;
          }
        },
      },
    },
    grid: {
      height: gridHeight,
      left: 70,
      right: 20,
      top: 10,
    },
    series: [
      {
        name: 'Current',
        type: 'bar',
        data: current?.map(item => item.current),
        itemStyle: {
          color: '#4B0082',
        },
      },
      {
        name: 'Accumulated',
        type: 'line',
        data: accumulated?.map(item => item.accumulated),
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 2,
        },
        areaStyle: {
          color: 'rgba(138,43,226,0.3)',
        },
      },
    ],
  };
};